// ─────────────────────────────────────────────────────────
// SELECTRA SOLAR — SITE DATA
// Every editable piece of copy, contact detail and placeholder
// image lives here. Replace values below with real client data.
// Images can be dropped into src/assets/images/ and imported,
// or swapped for hosted URLs.
// ─────────────────────────────────────────────────────────

export const brand = {
  name: "SELECTRA SOLAR",
  tagline: "POWERING WHAT MATTERS.",
  supporting:
    "Smarter solar energy for homes, businesses and a more sustainable tomorrow.",
};

export const locations = [
  {
    id: "location-one",
    slug: "location-one",
    code: "01",
    name: "Selectra Solar — Coimbatore",
    city: "Coimbatore",
    region: "Tamil Nadu",
    address: "142, Avinashi Road, Peelamedu, Coimbatore, Tamil Nadu 641004",
    phone: "+91 98765 43210",
    email: "coimbatore@selectrasolar.in",
    hours: "Mon – Sat, 9:30 AM – 6:30 PM",
    description:
      "Our founding studio and engineering hub. From here we design, plan and commission residential and commercial installations across western Tamil Nadu, backed by an in-house technical team and a dedicated project office.",
    stat: { label: "Installations", value: "480+" },
    image: "location-coimbatore",
  },
  {
    id: "location-two",
    slug: "location-two",
    code: "02",
    name: "Selectra Solar — Salem",
    city: "Salem",
    region: "Tamil Nadu",
    address: "27 Fort Main Road, Salem, Tamil Nadu 636001",
    phone: "+91 98765 12345",
    email: "salem@selectrasolar.in",
    hours: "Mon – Sat, 9:30 AM – 6:30 PM",
    description:
      "Our newest presence, built to serve growing demand across Salem and the surrounding districts. The Salem team focuses on rooftop residential systems and small-industrial rollouts, with the same design standard as Coimbatore.",
    stat: { label: "Installations", value: "210+" },
    image: "location-salem",
  },
];

export const services = [
  {
    id: "residential",
    number: "01",
    name: "Residential Solar",
    short: "Rooftop systems designed around how your home actually uses power.",
    description:
      "End-to-end residential solar — from load assessment and panel layout to net-metering paperwork and after-install monitoring. Built to blend into your roofline, not dominate it.",
    image: "service-residential",
  },
  {
    id: "commercial",
    number: "02",
    name: "Commercial Solar",
    short: "Lower operating costs for offices, retail and hospitality.",
    description:
      "Commercial-scale arrays engineered for predictable ROI, with financing guidance, structural assessment and minimal disruption to daily operations.",
    image: "service-commercial",
  },
  {
    id: "industrial",
    number: "03",
    name: "Industrial Solar",
    short: "High-capacity systems for manufacturing and warehousing.",
    description:
      "Large-footprint installations sized for heavy industrial loads, including ground-mount and captive-consumption setups with long-term maintenance contracts.",
    image: "service-industrial",
  },
  {
    id: "installation",
    number: "04",
    name: "Solar Panel Installation",
    short: "Certified installation crews and rigorous quality checks.",
    description:
      "Our own installation teams — not subcontracted labour — follow a documented QA process at every stage, from mounting to commissioning.",
    image: "service-installation",
  },
  {
    id: "maintenance",
    number: "05",
    name: "Solar Maintenance",
    short: "Scheduled servicing to keep every panel performing at its peak.",
    description:
      "Periodic cleaning, inverter health checks and performance audits, with proactive alerts if output ever drops below expected levels.",
    image: "service-maintenance",
  },
  {
    id: "consultation",
    number: "06",
    name: "Solar Consultation",
    short: "Independent guidance before you commit to a system.",
    description:
      "A no-pressure walkthrough of your options — system sizing, subsidy eligibility, payback period and financing — before any commercial conversation.",
    image: "service-consultation",
  },
];

export const energyConcepts = [
  {
    number: "01",
    title: "GENERATE",
    text: "Every panel is positioned and angled for maximum yield across the full arc of the day, not just peak sun hours.",
  },
  {
    number: "02",
    title: "OPTIMIZE",
    text: "Smart inverters and monitoring keep your system tuned as conditions, shading and consumption patterns change.",
  },
  {
    number: "03",
    title: "SUSTAIN",
    text: "Built for a 25-year lifespan, with maintenance plans designed to protect your investment for decades, not years.",
  },
];

export const whySelectra = [
  {
    number: "01",
    title: "QUALITY",
    text: "Tier-1 panels and inverters only, backed by manufacturer warranties we stand behind personally.",
  },
  {
    number: "02",
    title: "PRECISION",
    text: "Every system is engineered to your roof, your load and your budget — never a one-size template.",
  },
  {
    number: "03",
    title: "TRUST",
    text: "Transparent quotes, honest timelines, and a team that answers the phone after the invoice is paid.",
  },
  {
    number: "04",
    title: "LONG-TERM VALUE",
    text: "We design for the 25-year curve, not the install-day photo — savings that keep compounding.",
  },
];

export const projects = [
  {
    id: "proj-1",
    type: "RESIDENTIAL",
    location: "Salem",
    capacity: "5.5 kW",
    year: "2025",
    image: "project-1",
  },
  {
    id: "proj-2",
    type: "COMMERCIAL",
    location: "Coimbatore",
    capacity: "42 kW",
    year: "2025",
    image: "project-2",
  },
  {
    id: "proj-3",
    type: "INDUSTRIAL",
    location: "Erode",
    capacity: "180 kW",
    year: "2024",
    image: "project-3",
  },
  {
    id: "proj-4",
    type: "RESIDENTIAL",
    location: "Coimbatore",
    capacity: "3.2 kW",
    year: "2024",
    image: "project-4",
  },
  {
    id: "proj-5",
    type: "COMMERCIAL",
    location: "Salem",
    capacity: "28 kW",
    year: "2023",
    image: "project-5",
  },
];

export const testimonials = [
  {
    id: "t1",
    quote:
      "Selectra planned our rooftop system down to the last panel angle and delivered exactly what they promised, on time.",
    name: "Arun Kumaraswamy",
    location: "Coimbatore",
  },
  {
    id: "t2",
    quote:
      "Our factory's power bill dropped by more than a third within the first quarter after commissioning.",
    name: "Meera Ranganathan",
    location: "Erode",
  },
  {
    id: "t3",
    quote:
      "Clear communication from the first site visit to the final net-metering approval. No surprises.",
    name: "Suresh Palaniappan",
    location: "Salem",
  },
];

export const nav = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Locations", path: "/locations" },
  { label: "Projects", path: "/#projects" },
  { label: "Contact", path: "/contact" },
];

export const socials = [
  { label: "Instagram", href: "https://instagram.com" },
  { label: "LinkedIn", href: "https://linkedin.com" },
  { label: "Facebook", href: "https://facebook.com" },
];
